import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { hideComment, unhideComment } from '../../api/community';
import { hideStory, unhideStory } from '../../api/moderation';
import { claimReport, decideReport, listReports, type Report } from '../../api/reports';
import { Alert, Badge, Button, Card, EmptyState, LoadingState } from '../ui';

/** What acting on this report would do, said as the thing it does. */
function acting(entry: Report): string {
  if (entry.comment) {
    return entry.comment.is_hidden ? 'Put the comment back' : 'Take the comment out of view';
  }
  return entry.post.status === 'HIDDEN' ? 'Put the story back' : 'Take the story down';
}

function when(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * What people have reported and nobody has decided yet.
 *
 * A report is a claim: deciding one records that somebody looked, and never
 * touches what it names. Acting is a **separate decision**, with its own
 * endpoint, its own required reason and its own audit entry — which is what
 * stops a co-ordinated group from being the moderator.
 *
 * Both decisions are offered here, side by side and clearly apart, because the
 * alternative was worse than the risk of confusing them: a moderator who wanted
 * to act had to leave, open the story, and find the comment among up to five
 * hundred others. A separation nobody can follow in practice is not a
 * separation; the wording is what keeps the two straight.
 */
export function ReportQueue() {
  const [entries, setEntries] = useState<Report[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const page = await listReports();
      setEntries(page.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (id: string, action: () => Promise<unknown>) => {
    setWorking(id);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  /** Act on what a report names. Never automatic, and never without a reason. */
  const act = (entry: Report) => {
    const comment = entry.comment;
    const hidden = comment ? comment.is_hidden : entry.post.status === 'HIDDEN';

    if (hidden) {
      void run(entry.id, () => (comment ? unhideComment(comment.id) : unhideStory(entry.post.id)));
      return;
    }

    const reason = window.prompt(
      comment
        ? 'Why is this comment being taken out of view?\n\nIts author reads this. Nobody else does.'
        : 'Why is this story being taken down?\n\nIts author reads this.',
    );
    if (reason === null || !reason.trim()) {
      return;
    }
    void run(entry.id, () =>
      comment ? hideComment(comment.id, reason.trim()) : hideStory(entry.post.id, reason.trim()),
    );
  };

  const decide = (entry: Report, decision: 'resolve' | 'dismiss') => {
    const note = window.prompt(
      decision === 'resolve'
        ? 'What did you conclude, and what did you do about it?\n\nThe next moderator to look at this story reads this. The reporter does not.'
        : 'Why is there nothing to do here?\n\nThe next moderator to look at this story reads this. The reporter does not.',
    );
    if (note === null || !note.trim()) {
      return;
    }
    void run(entry.id, () => decideReport(entry.id, decision, note.trim()));
  };

  if (entries === null) {
    return <LoadingState label="Reading the reports…" />;
  }

  return (
    <>
      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      {entries.length === 0 ? (
        <EmptyState
          title="Nothing has been reported"
          description="When a reader tells us something is wrong with a story, it appears here."
        />
      ) : (
        <ul className="sc-queue">
          {entries.map((entry) => (
            <li key={entry.id}>
              <Card>
                <div className="sc-queue__head">
                  <div>
                    <h2 className="sc-queue__title">
                      <Link to={`/stories/${entry.post.id}`}>{entry.post.title || 'Untitled'}</Link>
                    </h2>
                    <p className="sc-queue__muted">
                      {entry.comment
                        ? `a comment by @${entry.comment.author}`
                        : `@${entry.post.author}`}{' '}
                      · reported by @{entry.reporter.handle} · {when(entry.created_at)}
                    </p>
                  </div>
                  <span className="sc-queue__labels">
                    <Badge tone="warning">{entry.kind_label}</Badge>
                    {entry.post.open_reports > 1 && (
                      <Badge tone="accent">{entry.post.open_reports} people</Badge>
                    )}
                    {entry.status === 'REVIEWING' && entry.moderator && (
                      <Badge tone="neutral">@{entry.moderator.handle} is on it</Badge>
                    )}
                  </span>
                </div>

                {/* The words themselves, in full: the decision is about these
                    exact ones, and the moderator cannot make it from a summary. */}
                {entry.comment && (
                  <blockquote className="sc-queue__quote">
                    {entry.comment.body}
                    {entry.comment.is_hidden && (
                      <span className="sc-queue__muted"> — already out of view</span>
                    )}
                  </blockquote>
                )}

                <p className="sc-queue__details">{entry.details}</p>

                <div className="sc-queue__actions">
                  {/* Acting, kept visually first and worded as what it does —
                      the two buttons after it only record that somebody
                      looked. */}
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === entry.id}
                    onClick={() => act(entry)}
                  >
                    {acting(entry)}
                  </Button>
                  {entry.status === 'OPEN' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      isLoading={working === entry.id}
                      onClick={() => void run(entry.id, () => claimReport(entry.id))}
                    >
                      I am looking at this
                    </Button>
                  )}
                  <Button
                    size="sm"
                    isLoading={working === entry.id}
                    onClick={() => decide(entry, 'resolve')}
                  >
                    Dealt with
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === entry.id}
                    onClick={() => decide(entry, 'dismiss')}
                  >
                    Nothing to do
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
